/**
 * The rule language, taught to Monaco.
 *
 * This is the adapter and nothing else: everything it knows about the language
 * comes from `powerFlow/language` — the tokens from the same grammar the parser
 * reads, the completions and go-to-definition targets from `service.ts`, the
 * squiggles from the compiler's own diagnostics. Monaco's job is to draw them.
 *
 * Providers are registered once, globally, because Monaco registers them per
 * *language* rather than per editor. What differs between editors — which
 * relations exist, how a draft is checked, where a relation's panel is — is
 * looked up by model, in {@link contexts}. Each editor registers its context on
 * mount and drops it on unmount, so a provider always answers with the rule set
 * the editor it was asked about belongs to.
 */

import * as monaco from 'monaco-editor/esm/vs/editor/editor.api';
// The editor's own contributions — the suggest widget, the hover, find,
// bracket matching. `editor.api` alone is the API surface with none of the UI;
// `edcore.main` adds the contributions but, unlike `editor.main`, none of the
// bundled languages (TypeScript, JSON, HTML…) that this app has no use for.
import 'monaco-editor/esm/vs/editor/edcore.main.js';
import { Catalog, RelationSchema } from '../../powerFlow/language/schema';
import { Diagnostic, Span } from '../../powerFlow/language/diagnostics';
import {
  Completion,
  Reference,
  completionsAt,
  referenceAt,
  relationReferenceAt,
  typeAt,
} from '../../powerFlow/language/service';
import { columnTypeOf, signatureOf, typeToString, underlyingScalar } from '../../powerFlow/language/types';

export { monaco };

// Monaco's background worker is fetched by URL rather than imported, so the
// build emits it as an entry point of its own (see build.js) and this says
// where it landed. It backs the editor's incremental text services; the
// language features above all run on the main thread.
declare const self: { MonacoEnvironment?: { getWorkerUrl(moduleId: string, label: string): string } };
if (typeof self !== 'undefined' && !self.MonacoEnvironment) {
  self.MonacoEnvironment = {
    getWorkerUrl: () => new URL('monaco.worker.js', document.baseURI).toString(),
  };
}

export const GRID_RULES_LANGUAGE = 'grid-rules';
export const GRID_RULES_THEME = 'grid-rules';

/** The scheme of a go-to-definition target: a relation's panel, not a file. */
const RELATION_SCHEME = 'grid-relation';

/** What a provider needs to answer for one editor's model. */
export interface RuleEditorContext {
  /** The relations this rule is checked against — completions and jumps read it. */
  catalog: Catalog;
  /** The problems with a draft, located in its text. */
  diagnose: (draft: string) => Diagnostic[];
  /**
   * Open a relation's panel, as clicking it in the tree would — highlighting one
   * of its columns, when the jump was from a column rather than the relation.
   */
  navigate: (relation: string, column?: string) => void;
}

const contexts = new Map<string, RuleEditorContext>();

export function setEditorContext(uri: string, context: RuleEditorContext): void {
  contexts.set(uri, context);
}

export function clearEditorContext(uri: string): void {
  contexts.delete(uri);
}

const contextFor = (model: monaco.editor.ITextModel | null | undefined): RuleEditorContext | undefined =>
  model ? contexts.get(model.uri.toString()) : undefined;

// ---------------------------------------------------------------------------
// Ranges
// ---------------------------------------------------------------------------

const rangeOf = (span: Span): monaco.IRange => ({
  startLineNumber: span.startLine,
  startColumn: span.startColumn,
  endLineNumber: span.endLine,
  endColumn: span.endColumn,
});

// ---------------------------------------------------------------------------
// Syntax highlighting
// ---------------------------------------------------------------------------

/**
 * A Monarch grammar mirroring parse.ts.
 *
 * The one thing worth spelling out is why the `def` head gets states of its
 * own: `:` introduces a parameter's relation inside the parameter list and ends
 * a block's test outside it, so `battery` in `b: battery` is a relation while
 * `x` in `if p: x` is a value. Tracking the head is what tells the two apart —
 * everywhere else, an identifier followed by `(` is a relation being applied and
 * an identifier after `in` is one being ranged over.
 */
const monarch: monaco.languages.IMonarchLanguage = {
  defaultToken: '',
  keywords: ['def', 'for', 'in', 'if', 'elif', 'else', 'and', 'minimize', 'maximize'],
  builtins: ['sqrt', 'min', 'max', 'sum'],
  tokenizer: {
    root: [
      [/\bdef\b/, 'keyword', '@defhead'],
      { include: '@expr' },
    ],
    defhead: [
      [/\(/, 'delimiter.parenthesis', '@params'],
      [/:/, 'delimiter', '@pop'],
      [/[A-Za-z_]\w*/, 'rulename'],
      [/\s+/, ''],
    ],
    params: [
      [/\)/, 'delimiter.parenthesis', '@pop'],
      [/\(/, 'delimiter.parenthesis', '@nested'],
      [/(:)(\s*)([A-Za-z_]\w*)/, ['delimiter', '', 'relation']],
      { include: '@expr' },
    ],
    // A filter's own parentheses, so the one that closes the parameter list is
    // still the one that ends it.
    nested: [
      [/\)/, 'delimiter.parenthesis', '@pop'],
      [/\(/, 'delimiter.parenthesis', '@push'],
      { include: '@expr' },
    ],
    expr: [
      [/\s+/, ''],
      [
        /(\bfor\b)(\s+)([A-Za-z_]\w*)(\s+)(\bin\b)(\s+)([A-Za-z_]\w*)/,
        ['keyword', '', 'variable', '', 'keyword', '', 'relation'],
      ],
      [/(\.)([A-Za-z_]\w*)/, ['delimiter', 'attribute']],
      [
        /[A-Za-z_]\w*(?=\s*\()/,
        { cases: { '@keywords': 'keyword', '@builtins': 'builtin', '@default': 'relation' } },
      ],
      [/[A-Za-z_]\w*/, { cases: { '@keywords': 'keyword', '@builtins': 'builtin', '@default': 'variable' } }],
      [/\d+(\.\d+)?/, 'number'],
      [/"([^"\\]|\\.)*"/, 'string'],
      [/'([^'\\]|\\.)*'/, 'string'],
      [/["']/, 'string.invalid'],
      [/==|!=|<=|>=|[<>+\-*/=]/, 'operator'],
      [/[(),:]/, 'delimiter'],
    ],
  },
};

/** Light, restrained, and legible against the panel's near-white background. */
const theme: monaco.editor.IStandaloneThemeData = {
  base: 'vs',
  inherit: true,
  rules: [
    { token: 'keyword', foreground: 'a626a4' },
    { token: 'rulename', foreground: '4078f2', fontStyle: 'bold' },
    { token: 'relation', foreground: '0184bc' },
    { token: 'builtin', foreground: '4078f2' },
    { token: 'attribute', foreground: '986801' },
    { token: 'variable', foreground: '383a42' },
    { token: 'number', foreground: 'c18401' },
    { token: 'string', foreground: '50a14f' },
    { token: 'string.invalid', foreground: 'c62828' },
    { token: 'operator', foreground: '383a42' },
    { token: 'delimiter', foreground: 'a0a1a7' },
  ],
  colors: {
    'editor.background': '#ffffff',
    'editorLineNumber.foreground': '#c8c8c8',
  },
};

// ---------------------------------------------------------------------------
// Completions
// ---------------------------------------------------------------------------

const COMPLETION_KINDS: Record<Completion['kind'], monaco.languages.CompletionItemKind> = {
  relation: monaco.languages.CompletionItemKind.Class,
  column: monaco.languages.CompletionItemKind.Field,
  variable: monaco.languages.CompletionItemKind.Variable,
  keyword: monaco.languages.CompletionItemKind.Keyword,
  function: monaco.languages.CompletionItemKind.Function,
};

/**
 * Relations, then columns and variables, then builtins, then keywords — the
 * order the list is shown in when everything matches the prefix equally.
 */
const SORT_ORDER: Record<Completion['kind'], string> = {
  variable: '1',
  column: '1',
  relation: '2',
  function: '3',
  keyword: '4',
};

// ---------------------------------------------------------------------------
// Definition targets
// ---------------------------------------------------------------------------

/**
 * The document a definition points at: one line saying what's there.
 *
 * There has to be *something* there. Before opening a definition Monaco
 * resolves its target's text — that's what the preview under a ⌘-hover shows —
 * and a URI with no model behind it fails that resolve. Since a line has to
 * exist, it may as well be the line worth reading: the relation's signature, or
 * for a column the column and its type.
 *
 * The column rides in the URI's fragment, so a column and its relation are
 * different targets with different previews, and the opener can tell them apart.
 */
function definitionTarget(
  reference: Reference,
  catalog: Catalog,
): { uri: monaco.Uri; length: number } | undefined {
  const schema = catalog[reference.relation];
  if (!schema) return undefined;
  const uri = monaco.Uri.from({
    scheme: RELATION_SCHEME,
    path: `/${schema.name}`,
    fragment: reference.kind === 'column' ? reference.column : '',
  });
  const text =
    reference.kind === 'column'
      ? `${schema.name}.${reference.column}: ${typeToString(columnTypeOf(schema, reference.column, catalog))}`
      : `${signatureOf(schema, catalog)}   (${schema.kind === 'decision' ? 'decision variable' : 'data'})`;
  const existing = monaco.editor.getModel(uri);
  if (!existing) monaco.editor.createModel(text, 'plaintext', uri);
  else if (existing.getValue() !== text) existing.setValue(text);
  return { uri, length: text.length };
}

// ---------------------------------------------------------------------------
// Hover contents
// ---------------------------------------------------------------------------

/**
 * What a hover says about a relation: its signature, what fills it, and — when
 * it's being shown as some expression's *type* — the scalar it is underneath.
 *
 * That last line is the honest part of a reference type. `l.from` is a `bus`,
 * which is a name for a string; the name is what makes comparing it to a `gen`
 * a mistake, and the string is what actually gets compared.
 */
function relationContents(
  schema: RelationSchema,
  catalog: Catalog,
  asTypeOf?: string,
): monaco.IMarkdownString[] {
  const contents: monaco.IMarkdownString[] = [{ value: `\`${signatureOf(schema, catalog)}\`` }];
  contents.push({
    value: schema.kind === 'decision' ? 'A decision variable — the solver assigns it.' : 'Data.',
  });
  if (asTypeOf) {
    const scalar = underlyingScalar({ kind: 'relation', name: asTypeOf }, catalog);
    if (scalar.kind === 'scalar') {
      const article = scalar.name === 'int' ? 'an' : 'a';
      contents.push({ value: `A \`${asTypeOf}\` is ${article} \`${scalar.name}\` underneath.` });
    }
  }
  return contents;
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

let registered = false;

/** Register the language, its theme and its providers. Safe to call repeatedly. */
export function ensureGridRulesLanguage(): void {
  if (registered) return;
  registered = true;

  monaco.languages.register({ id: GRID_RULES_LANGUAGE });
  monaco.languages.setMonarchTokensProvider(GRID_RULES_LANGUAGE, monarch);
  monaco.editor.defineTheme(GRID_RULES_THEME, theme);

  monaco.languages.setLanguageConfiguration(GRID_RULES_LANGUAGE, {
    brackets: [['(', ')']],
    autoClosingPairs: [
      { open: '(', close: ')' },
      { open: '"', close: '"' },
      { open: "'", close: "'" },
    ],
    surroundingPairs: [
      { open: '(', close: ')' },
      { open: '"', close: '"' },
      { open: "'", close: "'" },
    ],
    // A body is indented under its head, the way the printer writes it.
    onEnterRules: [
      {
        beforeText: /:\s*$/,
        action: { indentAction: monaco.languages.IndentAction.Indent },
      },
    ],
  });

  monaco.languages.registerCompletionItemProvider(GRID_RULES_LANGUAGE, {
    // A dot asks for a column and a colon for a relation, and neither starts a
    // word, so neither would open the list on its own.
    triggerCharacters: ['.', ':'],
    provideCompletionItems(model, position) {
      const context = contextFor(model);
      if (!context) return { suggestions: [] };
      const { items, replace } = completionsAt(
        model.getValue(),
        { line: position.lineNumber, column: position.column },
        context.catalog,
      );
      const range = rangeOf(replace);
      return {
        suggestions: items.map((item) => ({
          label: item.label,
          kind: COMPLETION_KINDS[item.kind],
          detail: item.detail,
          insertText: item.insertText ?? item.label,
          insertTextRules: item.insertText
            ? monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet
            : undefined,
          sortText: `${SORT_ORDER[item.kind]}${item.label}`,
          range,
        })),
      };
    },
  });

  // Go to definition: the "definition" of a relation is the panel that holds it
  // — its table, its rule — and of a column, that same panel with the column
  // picked out. Either way the target is a URI the opener below turns into a
  // navigation rather than a place in another file.
  monaco.languages.registerDefinitionProvider(GRID_RULES_LANGUAGE, {
    provideDefinition(model, position) {
      const context = contextFor(model);
      if (!context) return null;
      const reference = referenceAt(
        model.getValue(),
        { line: position.lineNumber, column: position.column },
        context.catalog,
      );
      const target = reference && definitionTarget(reference, context.catalog);
      if (!reference || !target) return null;
      return [
        {
          uri: target.uri,
          // The whole line, which is what the ⌘-hover preview shows.
          range: { startLineNumber: 1, startColumn: 1, endLineNumber: 1, endColumn: target.length + 1 },
          // Nothing, deliberately. After the jump, Monaco flashes this range in
          // "the editor the definition is in" — and because our opener handles
          // the navigation itself, the editor it's handed back is the one you
          // ⌘-clicked in. An empty range flashes nothing; without it, the
          // target's first line lands as a highlight over the rule's first line.
          targetSelectionRange: { startLineNumber: 1, startColumn: 1, endLineNumber: 1, endColumn: 1 },
          originSelectionRange: rangeOf(reference.span),
        },
      ];
    },
  });

  // Hovering a relation shows what it holds — the same signature the completion
  // list gives, for the name already written.
  // Hovering says what something is. A relation name gives its signature; any
  // other expression gives its type — which for a row variable or a reference
  // is a relation, so that comes with the signature too.
  monaco.languages.registerHoverProvider(GRID_RULES_LANGUAGE, {
    provideHover(model, position) {
      const context = contextFor(model);
      if (!context) return null;
      const source = model.getValue();
      const at = { line: position.lineNumber, column: position.column };

      const reference = relationReferenceAt(source, at, context.catalog);
      const referenced = reference && context.catalog[reference.relation];
      if (reference && referenced) {
        return { range: rangeOf(reference.span), contents: relationContents(referenced, context.catalog) };
      }

      const typed = typeAt(source, at, context.catalog);
      if (!typed) return null;
      const contents: monaco.IMarkdownString[] = [
        { value: `\`\`\`\n${typed.text}: ${typeToString(typed.type)}\n\`\`\`` },
      ];
      // A relation type is a row, so say which row — and what it is once you
      // look through the name, since that's what arithmetic and comparison see.
      if (typed.type.kind === 'relation') {
        const schema = context.catalog[typed.type.name];
        if (schema) contents.push(...relationContents(schema, context.catalog, typed.type.name));
      }
      return { range: rangeOf(typed.span), contents };
    },
  });

  // Ctrl/⌘-click and F12 end up here with the target the definition provider
  // returned. Relation targets are the app's own navigation; anything else is
  // left to Monaco.
  monaco.editor.registerEditorOpener({
    openCodeEditor(source, resource) {
      if (resource.scheme !== RELATION_SCHEME) return false;
      const context = contextFor(source.getModel());
      if (!context) return false;
      // Deferred, because opening another panel unmounts *this* editor while
      // Monaco is still finishing the ⌘-click that got us here — pulling it out
      // from under itself, which it logs as a cancellation. The jump lands
      // either way; a beat's delay just keeps the console honest.
      const relation = resource.path.replace(/^\//, '');
      const column = resource.fragment || undefined;
      setTimeout(() => context.navigate(relation, column), 50);
      return true;
    },
  });
}

/** Show a draft's diagnostics as squiggles under the text they're about. */
export function setDiagnostics(model: monaco.editor.ITextModel, diagnostics: Diagnostic[]): void {
  monaco.editor.setModelMarkers(
    model,
    GRID_RULES_LANGUAGE,
    diagnostics.map((diagnostic) => {
      // A message with no span is about the rule as a whole; underline its first
      // line rather than dropping it on the floor.
      const span = diagnostic.span ?? {
        startLine: 1,
        startColumn: 1,
        endLine: 1,
        endColumn: model.getLineMaxColumn(1),
      };
      return {
        ...rangeOf(span),
        message: diagnostic.message,
        severity: monaco.MarkerSeverity.Error,
      };
    }),
  );
}
